import { useState } from "react";
import type {
  CharacterState,
  Rules,
  StandaloneGift,
  ResurrectionPact,
} from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { buildShareUrl } from "@/lib/characterUrl";
import { getValidationIssues } from "@/lib/characterValidation";
import { emptyPact } from "./shared";
import { UltimateWarlockPanel } from "./UltimateWarlock";
import { OfferingGrid } from "./OfferingGrid";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Check,
  Copy,
  Eye,
  Plus,
  Minus,
  Trash2,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Lock,
  RefreshCw,
} from "lucide-react";

type GiftFlowState = {
  path?: "mission" | "exchange";
  patronId?: string;
  patronChosen?: boolean; // mission rewards: patron (or "None") picked
  exchangeOfferingId?: number;
  giftId?: string;
  giftSkillId?: string;
  choiceSwapFromId?: number;
  choiceSwapToId?: number;
};

export function StepReviewShare({
  state,
  rules,
  stats,
  onChange,
  copied,
  setCopied,
  onViewSheet,
}: {
  state: CharacterState;
  rules: Rules;
  stats: ReturnType<typeof calcDerivedStats>;
  onChange: (s: CharacterState) => void;
  copied: boolean;
  setCopied: (v: boolean) => void;
  onViewSheet: () => void;
}) {
  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];
  const { maxHp, maxWealthFromSkills } = rules.limits;
  const maxWealthName = rules.wealthLevels[maxWealthFromSkills]?.name;
  const validationIssues = getValidationIssues(state, stats, rules);

  const handleCopy = () => {
    navigator.clipboard.writeText(buildShareUrl(state)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const [addGiftFlow, setAddGiftFlow] = useState<GiftFlowState | null>(null);
  const [addOfferingOpen, setAddOfferingOpen] = useState(false);
  const [addResurrectionFlow, setAddResurrectionFlow] =
    useState<Partial<ResurrectionPact> | null>(null);

  // ── Section 1: Bonus Skill Points ────────────────────────────────────────
  const remaining = stats.totalSkillPoints - stats.usedSkillPoints;

  const getSkillCount = (skillId: string) =>
    state.coreSkillSelections.find((s) => s.skillId === skillId)?.count ?? 0;

  const setSkillCount = (skillId: string, count: number) => {
    const others = state.coreSkillSelections.filter(
      (s) => s.skillId !== skillId
    );
    onChange({
      ...state,
      coreSkillSelections: count > 0 ? [...others, { skillId, count }] : others,
    });
  };

  const canAddSkill = (skillId: string): boolean => {
    if (remaining <= 0) return false;
    const skill = rules.coreSkills.find((s) => s.id === skillId);
    if (!skill) return false;
    if (!skill.repeatable) {
      if (getSkillCount(skillId) > 0) return false;
      const claimedByGift = [...state.pacts, ...(state.bonusPacts || [])].some(
        (p) =>
          p.giftId === "gift-of-earthly-experience" && p.giftSkillId === skillId
      );
      if (claimedByGift) return false;
    }
    if (
      skill.maxTimes !== undefined &&
      getSkillCount(skillId) >= skill.maxTimes
    )
      return false;
    if (skillId === "endurance-training") return stats.hp < maxHp;
    if (skillId === "inherited-wealth")
      return stats.wealthLevel < maxWealthFromSkills;
    return true;
  };

  // ── Section 3: Standalone Gifts helpers ──────────────────────────────────
  const standaloneGifts = state.standaloneGifts || [];

  const usedNonRepeatableGiftSkillIds = [
    ...allPacts
      .filter((p) => p.giftId === "gift-of-earthly-experience" && p.giftSkillId)
      .map((p) => p.giftSkillId!)
      .filter((id) => !rules.coreSkills.find((s) => s.id === id)?.repeatable),
    ...standaloneGifts
      .filter((g) => g.giftId === "gift-of-earthly-experience" && g.giftSkillId)
      .map((g) => g.giftSkillId!)
      .filter((id) => !rules.coreSkills.find((s) => s.id === id)?.repeatable),
    ...state.coreSkillSelections
      .map((s) => s.skillId)
      .filter((id) => !rules.coreSkills.find((s) => s.id === id)?.repeatable),
  ];

  const usedLimitedStandaloneGiftIds = standaloneGifts
    .map((g) => g.giftId)
    .filter((id) => {
      const gift = rules.otherworldlyGifts.find((g) => g.id === id);
      return gift && !gift.unlimited;
    });

  const flowStep = (flow: GiftFlowState): number => {
    if (!flow.path) return 1;
    if (flow.path === "mission" && !flow.patronChosen) return 2;
    if (flow.path === "exchange" && !flow.patronId) return 2;
    if (flow.path === "exchange" && !flow.exchangeOfferingId) return 3;
    if (!flow.giftId) return 4;
    if (flow.giftId === "gift-of-earthly-experience" && !flow.giftSkillId)
      return 5;
    if (flow.giftId === "gift-of-choice" && !flow.choiceSwapFromId) return 6;
    if (flow.giftId === "gift-of-choice" && !flow.choiceSwapToId) return 7;
    return 8;
  };

  const handleFlowUpdate = (newFlow: GiftFlowState) => {
    if (flowStep(newFlow) === 8) {
      const newGift: StandaloneGift = {
        giftId: newFlow.giftId!,
        giftSkillId: newFlow.giftSkillId || undefined,
        patronId: newFlow.patronId || undefined,
        exchangeOfferingId: newFlow.exchangeOfferingId || undefined,
        choiceSwapFromId: newFlow.choiceSwapFromId || undefined,
        choiceSwapToId: newFlow.choiceSwapToId || undefined,
        source: newFlow.path,
      };
      let newState: CharacterState = {
        ...state,
        standaloneGifts: [...standaloneGifts, newGift],
      };
      if (newFlow.path === "exchange" && newFlow.exchangeOfferingId) {
        newState = {
          ...newState,
          standaloneOfferings: [
            ...(state.standaloneOfferings || []),
            newFlow.exchangeOfferingId,
          ],
        };
      }
      onChange(newState);
      setAddGiftFlow(null);
    } else {
      setAddGiftFlow(newFlow);
    }
  };

  // ── Section 4: Won Back Soul ──────────────────────────────────────────────
  const swapMap = new Map<number, number>();
  for (const pact of allPacts) {
    if (
      pact.giftId === "gift-of-choice" &&
      pact.choiceSwapFromId &&
      pact.choiceSwapToId
    ) {
      swapMap.set(pact.choiceSwapFromId, pact.choiceSwapToId);
    }
  }
  for (const sg of standaloneGifts) {
    if (
      sg.giftId === "gift-of-choice" &&
      sg.choiceSwapFromId &&
      sg.choiceSwapToId
    ) {
      swapMap.set(sg.choiceSwapFromId, sg.choiceSwapToId);
    }
  }
  // Build full instance list (duplicates preserved — same offering can appear multiple times)
  const offeringInstances: number[] = [];
  for (const pact of allPacts) {
    if (!pact.darkOfferingId) continue;
    offeringInstances.push(
      swapMap.get(pact.darkOfferingId) ?? pact.darkOfferingId
    );
  }
  for (const id of state.standaloneOfferings || []) {
    offeringInstances.push(id);
  }
  for (const rp of state.resurrectionPacts || []) {
    offeringInstances.push(rp.darkOfferingId);
  }
  // Per-ID totals and won-back counts
  const offeringTotals = new Map<number, number>();
  for (const id of offeringInstances)
    offeringTotals.set(id, (offeringTotals.get(id) || 0) + 1);
  const wonBackCountsUI = new Map<number, number>();
  for (const id of state.wonBackOfferings || [])
    wonBackCountsUI.set(id, (wonBackCountsUI.get(id) || 0) + 1);
  // Unique IDs in first-appearance order
  const uniqueOfferingIds: number[] = [];
  {
    const seen = new Set<number>();
    for (const id of offeringInstances) {
      if (!seen.has(id)) {
        seen.add(id);
        uniqueOfferingIds.push(id);
      }
    }
  }

  const addWonBack = (id: number) =>
    onChange({
      ...state,
      wonBackOfferings: [...(state.wonBackOfferings || []), id],
    });
  const removeWonBack = (id: number) => {
    const list = [...(state.wonBackOfferings || [])];
    list.splice(list.indexOf(id), 1);
    onChange({ ...state, wonBackOfferings: list });
  };

  // ── Section 5: Forced Offerings ──────────────────────────────────────────
  const standaloneOfferings = state.standaloneOfferings || [];
  // An offering is "currently active" if (total instances - won-back count) > 0
  const currentlyActiveOfferingIds = new Set<number>(
    uniqueOfferingIds.filter(
      (id) => (offeringTotals.get(id) || 0) - (wonBackCountsUI.get(id) || 0) > 0
    )
  );

  const dedupedOfferingsForSwap = uniqueOfferingIds;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold mb-1">Review & Share</h2>
        <p className="text-muted-foreground text-sm">
          Review your character and share it with others.
        </p>
      </div>

      {/* Validation banner */}
      {validationIssues.length > 0 && (
        <div
          className={`rounded-lg border px-4 py-3 flex gap-3 ${
            validationIssues.some((i) => i.severity === "error")
              ? "border-destructive/50 bg-destructive/10 text-destructive"
              : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          }`}
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold">Character is incomplete</p>
            <ul className="space-y-0.5">
              {validationIssues.map((issue, i) => (
                <li key={i} className="text-sm flex items-start gap-1.5">
                  <span className="shrink-0">•</span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Quick summary */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-lg">
              {state.name || (
                <span className="text-muted-foreground italic">
                  No name set
                </span>
              )}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xl font-bold text-red-500">{stats.hp}</div>
              <div className="text-xs text-muted-foreground">HP</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-500">{stats.mp}</div>
              <div className="text-xs text-muted-foreground">MP</div>
            </div>
            <div>
              <div className="text-xl font-bold text-yellow-500">
                {rules.wealthLevels[stats.wealthLevel]?.name}
              </div>
              <div className="text-xs text-muted-foreground">Wealth</div>
            </div>
          </div>

          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Skill Points</span>
              <span>
                {stats.usedSkillPoints} / {stats.totalSkillPoints}
              </span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Dark Offerings</span>
              <span>
                {stats.totalDarkOfferings} / {stats.darkOfferingsLimit}
              </span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Known Spells</span>
              <span>{stats.knownSpells.length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Core Skills summary */}
      {state.coreSkillSelections.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Core Skills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {state.coreSkillSelections.map((sel) => {
                const skill = rules.coreSkills.find(
                  (s) => s.id === sel.skillId
                );
                return skill ? (
                  <Badge key={sel.skillId} variant="secondary">
                    {skill.name}
                    {sel.count > 1 ? ` ×${sel.count}` : ""}
                  </Badge>
                ) : null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pacts summary */}
      {allPacts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pacts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {allPacts.map((pact, i) => {
              const offering = rules.darkOfferings.find(
                (o) => o.id === pact.darkOfferingId
              );
              const patron = rules.patrons.find((p) => p.id === pact.patronId);
              const gift = rules.otherworldlyGifts.find(
                (g) => g.id === pact.giftId
              );
              return (
                <div key={i} className="flex flex-wrap gap-1">
                  {offering && (
                    <Badge variant="destructive" className="text-xs">
                      {offering.name}
                    </Badge>
                  )}
                  {patron && (
                    <Badge variant="outline" className="text-xs">
                      {patron.name}
                    </Badge>
                  )}
                  {gift && (
                    <Badge variant="secondary" className="text-xs">
                      {gift.name}
                    </Badge>
                  )}
                  {!offering && !patron && !gift && (
                    <span className="text-xs text-muted-foreground italic">
                      Incomplete pact
                    </span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button className="flex-1" onClick={onViewSheet}>
          <Eye className="h-4 w-4 mr-2" />
          View Character Sheet
        </Button>
        <Button variant="outline" className="flex-1" onClick={handleCopy}>
          {copied ? (
            <Check className="h-4 w-4 mr-2" />
          ) : (
            <Copy className="h-4 w-4 mr-2" />
          )}
          {copied ? "Copied!" : "Copy Share Link"}
        </Button>
      </div>

      {/* Advanced / Returning Player */}
      <Card>
        <CardHeader className="pb-2">
          <button
            className="flex items-center justify-between w-full text-left"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <CardTitle className="text-sm">
              Advanced / Returning Player
            </CardTitle>
            <ChevronRight
              className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-90" : ""}`}
            />
          </button>
          <CardDescription className="text-xs">
            Manage post-creation gameplay changes
          </CardDescription>
        </CardHeader>
        {showAdvanced && (
          <CardContent className="space-y-6">
            {/* ── Section 1: Bonus Skill Points ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Bonus Skill Points
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() =>
                    onChange({
                      ...state,
                      bonusSkillPoints: Math.max(0, state.bonusSkillPoints - 1),
                    })
                  }
                  disabled={state.bonusSkillPoints <= 0}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center font-medium">
                  {state.bonusSkillPoints}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() =>
                    onChange({
                      ...state,
                      bonusSkillPoints: state.bonusSkillPoints + 1,
                    })
                  }
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              {remaining > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      <span
                        className={
                          remaining < 0
                            ? "text-destructive font-medium"
                            : "font-medium"
                        }
                      >
                        {remaining}
                      </span>
                      {" / "}
                      {stats.totalSkillPoints} points remaining
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setSkillPickerOpen((v) => !v)}
                    >
                      {skillPickerOpen ? "Hide" : "Show skill picker"}
                      {skillPickerOpen ? (
                        <ChevronUp className="h-3 w-3 ml-1" />
                      ) : (
                        <ChevronDown className="h-3 w-3 ml-1" />
                      )}
                    </Button>
                  </div>
                  {skillPickerOpen && (
                    <div className="space-y-1.5 border rounded-md p-2">
                      {rules.coreSkills.map((skill) => {
                        const count = getSkillCount(skill.id);
                        const selected = count > 0;
                        const claimedByGift =
                          !skill.repeatable &&
                          allPacts.some(
                            (p) =>
                              p.giftId === "gift-of-earthly-experience" &&
                              p.giftSkillId === skill.id
                          );
                        const addable = canAddSkill(skill.id);
                        const atHpCap =
                          skill.id === "endurance-training" &&
                          stats.hp >= maxHp;
                        const atWealthCap =
                          skill.id === "inherited-wealth" &&
                          stats.wealthLevel >= maxWealthFromSkills;
                        return (
                          <Card
                            key={skill.id}
                            className={`transition-colors ${selected ? "border-primary/50 bg-primary/5" : claimedByGift ? "opacity-60" : ""}`}
                          >
                            <CardContent className="pt-3 pb-2">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-xs">
                                      {skill.name}
                                    </span>
                                    {skill.repeatable && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        Repeatable
                                      </Badge>
                                    )}
                                    {skill.maxTimes && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        Max {skill.maxTimes}×
                                      </Badge>
                                    )}
                                    {claimedByGift && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs gap-1 text-muted-foreground"
                                      >
                                        <Lock className="h-2.5 w-2.5" />
                                        Taken by gift
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {skill.description}
                                  </p>
                                  {atHpCap && (
                                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      HP is at maximum ({maxHp})
                                    </p>
                                  )}
                                  {atWealthCap && (
                                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Wealth is at maximum ({maxWealthName})
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {skill.repeatable && selected ? (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="icon-sm"
                                        onClick={() =>
                                          setSkillCount(skill.id, count - 1)
                                        }
                                      >
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <span className="w-6 text-center text-sm font-medium">
                                        {count}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="icon-sm"
                                        onClick={() =>
                                          setSkillCount(skill.id, count + 1)
                                        }
                                        disabled={!addable}
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </>
                                  ) : selected ? (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => setSkillCount(skill.id, 0)}
                                    >
                                      <Check className="h-3 w-3 mr-1" />
                                      Selected
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      onClick={() => setSkillCount(skill.id, 1)}
                                      disabled={!addable}
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      Select
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* ── Section 2: Extra Starting Pacts ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Extra Starting Pacts (at character creation)
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => {
                    const newGifts = Math.max(0, state.bonusGifts - 1);
                    const trimmedBonusPacts = (state.bonusPacts || []).slice(
                      0,
                      newGifts
                    );
                    onChange({
                      ...state,
                      bonusGifts: newGifts,
                      bonusPacts: trimmedBonusPacts,
                    });
                  }}
                  disabled={state.bonusGifts <= 0}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center font-medium">
                  {state.bonusGifts}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() =>
                    onChange({
                      ...state,
                      bonusGifts: state.bonusGifts + 1,
                      bonusPacts: [...(state.bonusPacts || []), emptyPact()],
                    })
                  }
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              {state.bonusGifts > 0 && (
                <p className="text-xs text-muted-foreground">
                  Configure these in the Dark Offerings and Gifts steps.
                </p>
              )}
            </div>
            {/* ── Section 3: Bonus Gifts from Gameplay ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Bonus Gifts from Gameplay
              </Label>

              {/* Existing standalone gifts */}
              {standaloneGifts.length > 0 && (
                <div className="space-y-1.5">
                  {standaloneGifts.map((sg, i) => {
                    const gift = rules.otherworldlyGifts.find(
                      (g) => g.id === sg.giftId
                    );
                    const patron = rules.patrons.find(
                      (p) => p.id === sg.patronId
                    );
                    const exchangeOffering = rules.darkOfferings.find(
                      (o) => o.id === sg.exchangeOfferingId
                    );
                    return (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-2 rounded-md border p-2.5"
                      >
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-xs">
                              {gift?.name ?? sg.giftId}
                            </span>
                            {sg.source === "mission" && (
                              <Badge variant="secondary" className="text-xs">
                                Mission Reward
                              </Badge>
                            )}
                            {sg.source === "exchange" && (
                              <Badge variant="outline" className="text-xs">
                                Patron Exchange
                              </Badge>
                            )}
                            {sg.source !== "exchange" && (
                              <select
                                aria-label="Patron who granted this reward"
                                className="border rounded-md bg-background px-1.5 py-0.5 text-xs"
                                value={sg.patronId ?? ""}
                                onChange={(e) =>
                                  onChange({
                                    ...state,
                                    standaloneGifts: standaloneGifts.map(
                                      (g, idx) =>
                                        idx === i
                                          ? {
                                              ...g,
                                              patronId:
                                                e.target.value || undefined,
                                            }
                                          : g
                                    ),
                                  })
                                }
                              >
                                <option value="">No Patron</option>
                                {rules.patrons.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                    {p.exiled ? " (Previous Patron)" : ""}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                          {sg.source === "exchange" && (
                            <div className="flex items-center gap-1 flex-wrap text-xs text-muted-foreground">
                              {patron && (
                                <span>
                                  Patron:{" "}
                                  <span className="font-medium text-foreground">
                                    {patron.name}
                                  </span>
                                </span>
                              )}
                              {exchangeOffering && (
                                <span>
                                  • Offering:{" "}
                                  <span className="font-medium text-foreground">
                                    {exchangeOffering.name}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}
                          {gift && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {gift.description}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          onClick={() => {
                            const newGifts = standaloneGifts.filter(
                              (_, idx) => idx !== i
                            );
                            let newState: CharacterState = {
                              ...state,
                              standaloneGifts: newGifts,
                            };
                            if (
                              sg.source === "exchange" &&
                              sg.exchangeOfferingId
                            ) {
                              const idx = (
                                state.standaloneOfferings || []
                              ).indexOf(sg.exchangeOfferingId);
                              if (idx !== -1) {
                                const newOfferings = [
                                  ...(state.standaloneOfferings || []),
                                ];
                                newOfferings.splice(idx, 1);
                                newState = {
                                  ...newState,
                                  standaloneOfferings: newOfferings,
                                };
                              }
                            }
                            onChange(newState);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {standaloneGifts.length === 0 && !addGiftFlow && (
                <p className="text-xs text-muted-foreground italic">
                  No standalone gifts added.
                </p>
              )}

              {/* Multi-step Add Gift flow */}
              {addGiftFlow !== null ? (
                <div className="rounded-md border p-3 space-y-3">
                  {/* Step 1: choose path */}
                  {flowStep(addGiftFlow) === 1 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium">
                        How was this gift acquired?
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleFlowUpdate({
                              ...addGiftFlow,
                              path: "mission",
                            })
                          }
                        >
                          Mission Reward
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleFlowUpdate({
                              ...addGiftFlow,
                              path: "exchange",
                            })
                          }
                        >
                          Patron Exchange
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAddGiftFlow(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Exchange — choose patron */}
                  {flowStep(addGiftFlow) === 2 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          {addGiftFlow.path === "mission"
                            ? "Which Patron granted this reward?"
                            : "Select the patron"}
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddGiftFlow({ ...addGiftFlow, path: undefined })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      {addGiftFlow.path === "mission" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-xs"
                          onClick={() =>
                            handleFlowUpdate({
                              ...addGiftFlow,
                              patronId: undefined,
                              patronChosen: true,
                            })
                          }
                        >
                          None — not tied to a Patron
                        </Button>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rules.patrons.map((patron) => (
                          <button
                            key={patron.id}
                            onClick={() =>
                              handleFlowUpdate({
                                ...addGiftFlow,
                                patronId: patron.id,
                                patronChosen: true,
                              })
                            }
                            className="text-left rounded border p-2 hover:border-primary hover:bg-accent transition-colors w-full cursor-pointer"
                          >
                            <div className="font-semibold text-xs flex items-center gap-2">
                              {patron.name}
                              {patron.exiled && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  Previous Patron
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {patron.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Exchange — choose offering given */}
                  {flowStep(addGiftFlow) === 3 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Which offering was given to{" "}
                          {
                            rules.patrons.find(
                              (p) => p.id === addGiftFlow.patronId
                            )?.name
                          }
                          ?
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddGiftFlow({
                              ...addGiftFlow,
                              patronId: undefined,
                            })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      <OfferingGrid
                        offerings={rules.darkOfferings}
                        onSelect={(id) =>
                          handleFlowUpdate({
                            ...addGiftFlow,
                            exchangeOfferingId: id,
                          })
                        }
                        disabledIds={currentlyActiveOfferingIds}
                      />
                    </div>
                  )}

                  {/* Step 4: choose gift */}
                  {flowStep(addGiftFlow) === 4 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium">Choose the gift</p>
                          {(addGiftFlow.path === "exchange" ||
                            addGiftFlow.patronId) && (
                            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                              {addGiftFlow.patronId && (
                                <Badge variant="outline" className="text-xs">
                                  {
                                    rules.patrons.find(
                                      (p) => p.id === addGiftFlow.patronId
                                    )?.name
                                  }
                                </Badge>
                              )}
                              {addGiftFlow.exchangeOfferingId && (
                                <Badge
                                  variant="destructive"
                                  className="text-xs"
                                >
                                  {
                                    rules.darkOfferings.find(
                                      (o) =>
                                        o.id === addGiftFlow.exchangeOfferingId
                                    )?.name
                                  }
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            if (addGiftFlow.path === "exchange") {
                              setAddGiftFlow({
                                ...addGiftFlow,
                                exchangeOfferingId: undefined,
                              });
                            } else {
                              setAddGiftFlow({
                                ...addGiftFlow,
                                patronId: undefined,
                                patronChosen: false,
                              });
                            }
                          }}
                        >
                          ← Back
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {rules.otherworldlyGifts.map((gift) => {
                          const locked =
                            !gift.unlimited &&
                            usedLimitedStandaloneGiftIds.includes(gift.id);
                          return (
                            <button
                              key={gift.id}
                              disabled={locked}
                              onClick={() =>
                                handleFlowUpdate({
                                  ...addGiftFlow,
                                  giftId: gift.id,
                                })
                              }
                              className={`text-left rounded border p-2.5 transition-colors w-full ${
                                locked
                                  ? "opacity-35 cursor-not-allowed bg-muted/20"
                                  : "hover:border-primary hover:bg-accent cursor-pointer"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-semibold text-xs">
                                      {gift.name}
                                    </span>
                                    {gift.unlimited && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        Unlimited
                                      </Badge>
                                    )}
                                    {locked && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs gap-1 text-muted-foreground"
                                      >
                                        <Lock className="h-2.5 w-2.5" />
                                        Already taken
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                    {gift.description}
                                  </p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 5: Gift of Earthly Experience — choose skill */}
                  {flowStep(addGiftFlow) === 5 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Choose a core skill (Gift of Earthly Experience)
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddGiftFlow({
                              ...addGiftFlow,
                              giftId: undefined,
                            })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {rules.coreSkills.map((skill) => {
                          const atCap =
                            (skill.id === "endurance-training" &&
                              stats.hp >= maxHp) ||
                            (skill.id === "inherited-wealth" &&
                              stats.wealthLevel >= maxWealthFromSkills);
                          const skillLocked =
                            atCap ||
                            (!skill.repeatable &&
                              usedNonRepeatableGiftSkillIds.includes(skill.id));
                          const capReason =
                            skill.id === "endurance-training" && atCap
                              ? `HP at max (${maxHp})`
                              : skill.id === "inherited-wealth" && atCap
                                ? `Wealth at max (${maxWealthName})`
                                : null;
                          return (
                            <button
                              key={skill.id}
                              disabled={skillLocked}
                              onClick={() =>
                                handleFlowUpdate({
                                  ...addGiftFlow,
                                  giftSkillId: skill.id,
                                })
                              }
                              className={`text-left rounded border p-2 transition-colors w-full ${
                                skillLocked
                                  ? "opacity-35 cursor-not-allowed bg-muted/20"
                                  : "hover:border-amber-500/50 hover:bg-amber-500/5 cursor-pointer"
                              }`}
                            >
                              <div className="flex items-start gap-1.5">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-medium text-xs">
                                      {skill.name}
                                    </span>
                                    {skill.repeatable && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs py-0"
                                      >
                                        Repeatable
                                      </Badge>
                                    )}
                                    {skillLocked && !capReason && (
                                      <Lock className="h-3 w-3 text-muted-foreground" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                                    {skill.description}
                                  </p>
                                  {capReason && (
                                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
                                      <AlertTriangle className="h-2.5 w-2.5" />
                                      {capReason}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 6: Gift of Choice — choose offering to swap out */}
                  {flowStep(addGiftFlow) === 6 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Choose which offering to replace (Gift of Choice)
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddGiftFlow({
                              ...addGiftFlow,
                              giftId: undefined,
                            })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      {dedupedOfferingsForSwap.length === 0 && (
                        <p className="text-xs italic text-muted-foreground">
                          No offerings available to swap.
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {dedupedOfferingsForSwap.map((id) => {
                          const off = rules.darkOfferings.find(
                            (o) => o.id === id
                          );
                          if (!off) return null;
                          return (
                            <button
                              key={id}
                              onClick={() =>
                                handleFlowUpdate({
                                  ...addGiftFlow,
                                  choiceSwapFromId: id,
                                })
                              }
                              className="text-left rounded border p-2 hover:border-violet-500/50 hover:bg-violet-500/5 transition-colors w-full cursor-pointer"
                            >
                              <div className="font-semibold text-xs">
                                {off.name}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {off.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 7: Gift of Choice — choose replacement offering */}
                  {flowStep(addGiftFlow) === 7 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Replace{" "}
                          <span className="font-semibold">
                            {
                              rules.darkOfferings.find(
                                (o) => o.id === addGiftFlow.choiceSwapFromId
                              )?.name
                            }
                          </span>{" "}
                          with:
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddGiftFlow({
                              ...addGiftFlow,
                              choiceSwapFromId: undefined,
                            })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rules.darkOfferings
                          .filter((o) => o.id !== addGiftFlow.choiceSwapFromId)
                          .map((off) => (
                            <button
                              key={off.id}
                              onClick={() =>
                                handleFlowUpdate({
                                  ...addGiftFlow,
                                  choiceSwapToId: off.id,
                                })
                              }
                              className="text-left rounded border p-2 hover:border-violet-500/50 hover:bg-violet-500/5 transition-colors w-full cursor-pointer"
                            >
                              <div className="font-semibold text-xs">
                                {off.name}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {off.description}
                              </p>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setAddGiftFlow({})}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Gift
                </Button>
              )}
            </div>
            {/* ── Section 3b: Resurrection ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Died &amp; Returned to Life
              </Label>
              <p className="text-xs text-muted-foreground">
                If your character has died and returned to life, they must give
                a Dark Offering to a Patron as the price of resurrection. This
                grants access to that Patron&apos;s challenges but no gift.
              </p>

              {/* Existing resurrection pacts */}
              {(state.resurrectionPacts || []).length > 0 && (
                <div className="space-y-1.5">
                  {(state.resurrectionPacts || []).map((rp, i) => {
                    const offering = rules.darkOfferings.find(
                      (o) => o.id === rp.darkOfferingId
                    );
                    const patron = rules.patrons.find(
                      (p) => p.id === rp.patronId
                    );
                    return (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-2 rounded-md border p-2.5"
                      >
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-xs">
                              {offering?.name ??
                                `Offering #${rp.darkOfferingId}`}
                            </span>
                            <Badge variant="destructive" className="text-xs">
                              Resurrection
                            </Badge>
                            {patron && (
                              <Badge variant="outline" className="text-xs">
                                {patron.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          onClick={() => {
                            const updated = (
                              state.resurrectionPacts || []
                            ).filter((_, idx) => idx !== i);
                            onChange({
                              ...state,
                              resurrectionPacts: updated.length
                                ? updated
                                : undefined,
                            });
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add resurrection flow */}
              {addResurrectionFlow !== null ? (
                <div className="rounded-md border p-3 space-y-3">
                  {!addResurrectionFlow.darkOfferingId ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Select the Dark Offering given
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => setAddResurrectionFlow(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <OfferingGrid
                        offerings={rules.darkOfferings}
                        selectedId={undefined}
                        onSelect={(id) =>
                          setAddResurrectionFlow({
                            ...addResurrectionFlow,
                            darkOfferingId: id,
                          })
                        }
                        disabledIds={currentlyActiveOfferingIds}
                      />
                    </div>
                  ) : !addResurrectionFlow.patronId ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">
                          Select the Patron who granted resurrection
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            setAddResurrectionFlow({
                              ...addResurrectionFlow,
                              darkOfferingId: undefined,
                            })
                          }
                        >
                          ← Back
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rules.patrons.map((patron) => (
                          <button
                            key={patron.id}
                            onClick={() => {
                              const newPact: ResurrectionPact = {
                                darkOfferingId:
                                  addResurrectionFlow.darkOfferingId!,
                                patronId: patron.id,
                              };
                              onChange({
                                ...state,
                                resurrectionPacts: [
                                  ...(state.resurrectionPacts || []),
                                  newPact,
                                ],
                              });
                              setAddResurrectionFlow(null);
                            }}
                            className="text-left rounded border p-2 hover:border-primary hover:bg-accent transition-colors w-full cursor-pointer"
                          >
                            <div className="font-semibold text-xs flex items-center gap-2">
                              {patron.name}
                              {patron.exiled && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  Previous Patron
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {patron.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setAddResurrectionFlow({})}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Resurrection
                </Button>
              )}
            </div>
            {/* ── Section 3c: Blood Sacrifice ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Blood Sacrifice
              </Label>
              <p className="text-xs text-muted-foreground">
                If you were a willing blood donor to resurrect another
                character, your max HP is permanently reduced by 1 per
                sacrifice.
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={(state.bloodSacrifices || 0) <= 0}
                  onClick={() =>
                    onChange({
                      ...state,
                      bloodSacrifices: Math.max(
                        0,
                        (state.bloodSacrifices || 0) - 1
                      ),
                    })
                  }
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center font-medium">
                  {state.bloodSacrifices || 0}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() =>
                    onChange({
                      ...state,
                      bloodSacrifices: (state.bloodSacrifices || 0) + 1,
                    })
                  }
                >
                  <Plus className="h-3 w-3" />
                </Button>
                {(state.bloodSacrifices || 0) > 0 && (
                  <span className="text-xs text-muted-foreground">
                    −{state.bloodSacrifices} max HP
                  </span>
                )}
              </div>
            </div>
            {/* ── Section 4: Win Back Your Soul ── */}{" "}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Win Back Your Soul
              </Label>
              {uniqueOfferingIds.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  No active offerings to remove.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {uniqueOfferingIds.map((id) => {
                    const offering = rules.darkOfferings.find(
                      (o) => o.id === id
                    );
                    const total = offeringTotals.get(id) || 0;
                    const wonBack = wonBackCountsUI.get(id) || 0;
                    const active = total - wonBack;
                    return (
                      <div
                        key={id}
                        className={`rounded-md border p-2.5 transition-colors ${wonBack > 0 ? "border-green-500/40 bg-green-500/5" : ""}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <span className="font-semibold text-xs">
                              {offering?.name ?? `Offering #${id}`}
                            </span>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {active > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {active} Active
                                </Badge>
                              )}
                              {wonBack > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-green-600 border-green-500/60 gap-1"
                                >
                                  <RefreshCw className="h-2.5 w-2.5" />
                                  {wonBack} Won Back
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="outline"
                              size="icon-sm"
                              disabled={wonBack <= 0}
                              onClick={() => removeWonBack(id)}
                              title="Restore one instance"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-xs font-medium w-8 text-center">
                              {wonBack}/{total}
                            </span>
                            <Button
                              variant="outline"
                              size="icon-sm"
                              disabled={wonBack >= total}
                              onClick={() => addWonBack(id)}
                              title="Win back one instance"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* ── Section 5: Forced Offerings (Challenge of 13) ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Forced Offerings (Challenge of 13)
              </Label>
              {standaloneOfferings.length === 0 && !addOfferingOpen && (
                <p className="text-xs text-muted-foreground italic">
                  No forced offerings added.
                </p>
              )}
              {standaloneOfferings.length > 0 && (
                <div className="space-y-1.5">
                  {standaloneOfferings.map((id, i) => {
                    const offering = rules.darkOfferings.find(
                      (o) => o.id === id
                    );
                    return (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
                      >
                        <span className="text-xs font-medium">
                          {offering?.name ?? `Offering #${id}`}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 shrink-0"
                          onClick={() => {
                            const newOfferings = [...standaloneOfferings];
                            newOfferings.splice(i, 1);
                            onChange({
                              ...state,
                              standaloneOfferings: newOfferings,
                            });
                          }}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {addOfferingOpen ? (
                <div className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium">
                      Select a dark offering to force
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() => setAddOfferingOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                  <OfferingGrid
                    offerings={rules.darkOfferings}
                    onSelect={(id) => {
                      onChange({
                        ...state,
                        standaloneOfferings: [...standaloneOfferings, id],
                      });
                      setAddOfferingOpen(false);
                    }}
                    disabledIds={currentlyActiveOfferingIds}
                  />
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setAddOfferingOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Forced Offering
                </Button>
              )}
            </div>
            {/* ── Section 6: Ultimate Warlock ── */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ultimate Warlock
              </Label>
              <UltimateWarlockPanel state={state} onChange={onChange} />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
